import { useState, useEffect } from 'react';
import { collection, collectionGroup, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc, getDocs, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';

export function useOrganization() {
  const [organization, setOrganization] = useState(null);
  const [orgMember, setOrgMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (user) {
        // Fetch the user's document first to get their orgId
        const userDocRef = doc(db, 'users', user.uid);
        let unsubscribeDb;
        const unsubscribeUser = onSnapshot(userDocRef, (userSnap) => {
          const userData = userSnap.data();
          const userOrgId = userData?.orgId;

          if (unsubscribeDb) {
            unsubscribeDb();
          }

          if (userOrgId) {
            // Include orgId in the query so Firestore static analyzer doesn't crash on unbound fields
            const q = query(collection(db, 'org_members'), where('userId', '==', user.uid), where('orgId', '==', userOrgId));
            
            unsubscribeDb = onSnapshot(q, async (snapshot) => {
              if (!snapshot.empty) {
                const memberData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
                setOrgMember(memberData);
                
                try {
                  // Fetch the organization details
                  const orgDoc = await getDoc(doc(db, 'organizations', memberData.orgId));
                  if (orgDoc.exists()) {
                    setOrganization({ id: orgDoc.id, ...orgDoc.data() });
                  }
                  
                  // If user is an admin, fetch members and requests
                  if (memberData.role === 'admin' || memberData.role === 'Administrator') {
                    const membersQ = query(collection(db, 'org_members'), where('orgId', '==', memberData.orgId));
                    onSnapshot(membersQ, (memSnap) => {
                      setMembers(memSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                    }, (err) => {
                      console.error("Members snapshot error:", err);
                    });

                    const requestsQ = query(collection(db, 'org_requests'), where('orgId', '==', memberData.orgId), where('status', '==', 'pending'));
                    onSnapshot(requestsQ, (reqSnap) => {
                      setRequests(reqSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                    }, (err) => {
                      console.error("Requests snapshot error:", err);
                    });
                  }
                } catch (err) {
                  console.error("Error fetching organization data:", err);
                }
              } else {
                setOrganization(null);
                setOrgMember(null);
                setMembers([]);
                setRequests([]);
              }
              setLoading(false);
            }, (err) => {
              console.error("Org member query error:", err);
              setLoading(false);
            });
          } else {
            setOrganization(null);
            setOrgMember(null);
            setMembers([]);
            setRequests([]);
            setLoading(false);
          }
        });

        return () => {
          if (unsubscribeDb) unsubscribeDb();
          unsubscribeUser();
        };
      } else {
        setOrganization(null);
        setOrgMember(null);
        setMembers([]);
        setRequests([]);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const createOrganization = async (name, domain) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    // Create Org
    const orgRef = await addDoc(collection(db, 'organizations'), {
      name,
      domain: domain || null,
      ownerId: auth.currentUser.uid,
      licenseTier: 'enterprise',
      createdAt: new Date().toISOString()
    });

    // Add user as admin
    const memberId = `${auth.currentUser.uid}_${orgRef.id}`;
    await setDoc(doc(db, 'org_members', memberId), {
      userId: auth.currentUser.uid,
      userEmail: auth.currentUser.email,
      userName: auth.currentUser.displayName || auth.currentUser.email,
      orgId: orgRef.id,
      role: 'Administrator',
      teamIds: [],
      joinedAt: new Date().toISOString()
    });

    // Update user's document with orgId
    await updateDoc(doc(db, 'users', auth.currentUser.uid), {
      orgId: orgRef.id
    });

    return orgRef.id;
  };

  const checkDomainAndRequestAccess = async () => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    if (!auth.currentUser.email) throw new Error("No email associated with user");

    const domain = auth.currentUser.email.split('@')[1];
    if (!domain) return false;

    // Find org with this domain
    const orgsQ = query(collection(db, 'organizations'), where('domain', '==', domain));
    const orgsSnap = await getDocs(orgsQ);

    if (!orgsSnap.empty) {
      const targetOrg = orgsSnap.docs[0];
      
      // Check if request already exists
      const reqQ = query(collection(db, 'org_requests'), where('userId', '==', auth.currentUser.uid), where('orgId', '==', targetOrg.id));
      const reqSnap = await getDocs(reqQ);
      
      if (reqSnap.empty) {
        // Create request
        await addDoc(collection(db, 'org_requests'), {
          orgId: targetOrg.id,
          orgName: targetOrg.data().name,
          userId: auth.currentUser.uid,
          userEmail: auth.currentUser.email,
          userName: auth.currentUser.displayName || auth.currentUser.email,
          status: 'pending',
          createdAt: new Date().toISOString()
        });
      }
      return targetOrg.data().name; // Return name to notify user
    }
    return false;
  };

  const approveRequest = async (requestId, orgId, userId, userEmail, userName) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    // Add member
    const memberId = `${userId}_${orgId}`;
    await setDoc(doc(db, 'org_members', memberId), {
      userId,
      userEmail,
      userName,
      orgId,
      role: 'member',
      teamIds: [],
      joinedAt: new Date().toISOString()
    });

    // Update user's document with orgId
    await updateDoc(doc(db, 'users', userId), {
      orgId: orgId
    });
    
    // Update request
    await updateDoc(doc(db, 'org_requests', requestId), {
      status: 'approved',
      handledAt: new Date().toISOString()
    });
  };

  const rejectRequest = async (requestId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    await updateDoc(doc(db, 'org_requests', requestId), {
      status: 'rejected',
      handledAt: new Date().toISOString()
    });
  };

  const transferOwnership = async (newOwnerId) => {
    if (!auth.currentUser || !organization) throw new Error("Must be logged in and part of an org");
    if (organization.ownerId !== auth.currentUser.uid) throw new Error("Only the owner can transfer ownership");

    await updateDoc(doc(db, 'organizations', organization.id), {
      ownerId: newOwnerId
    });
  };

  const exportOrganizationData = async () => {
    if (!auth.currentUser || !organization) throw new Error("Must be logged in and part of an org");
    if (organization.ownerId !== auth.currentUser.uid) throw new Error("Only the owner can export organization data");

    // Fetch teams for export
    const teamsQ = query(collection(db, 'teams'), where('orgId', '==', organization.id));
    const teamsSnap = await getDocs(teamsQ);
    const orgTeams = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const data = {
      organization,
      members,
      teams: orgTeams,
      requests,
      snippets: []
    };

    // Query snippets metadata where orgId matches
    const snippetsQ = query(collectionGroup(db, 'snippets_metadata'), where('orgId', '==', organization.id));
    const snippetsSnap = await getDocs(snippetsQ);
    
    // Fetch snippets content
    for (const docSnap of snippetsSnap.docs) {
      const meta = docSnap.data();
      const userId = docSnap.ref.parent.parent.id;
      const contentDoc = await getDoc(doc(db, 'users', userId, 'snippets_content', docSnap.id));
      data.snippets.push({
        id: docSnap.id,
        metadata: meta,
        content: contentDoc.exists() ? contentDoc.data() : null
      });
    }

    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `${organization.name}_backup.json`);
    linkElement.click();
  };

  const deleteOrganization = async () => {
    if (!auth.currentUser || !organization) throw new Error("Must be logged in and part of an org");
    if (organization.ownerId !== auth.currentUser.uid) throw new Error("Only the owner can delete the organization");

    // 1. Delete Snippets
    const snippetsQ = query(collectionGroup(db, 'snippets_metadata'), where('orgId', '==', organization.id));
    const snippetsSnap = await getDocs(snippetsQ);
    const deletePromises = [];
    
    snippetsSnap.forEach((docSnap) => {
      const userId = docSnap.ref.parent.parent.id;
      deletePromises.push(deleteDoc(doc(db, 'users', userId, 'snippets_metadata', docSnap.id)));
      deletePromises.push(deleteDoc(doc(db, 'users', userId, 'snippets_content', docSnap.id)));
    });

    // 2. Delete Teams
    const teamsQ = query(collection(db, 'teams'), where('orgId', '==', organization.id));
    const teamsSnap = await getDocs(teamsQ);
    teamsSnap.docs.forEach(teamDoc => {
      deletePromises.push(deleteDoc(doc(db, 'teams', teamDoc.id)));
    });

    // 3. Delete Members and clear orgId from users
    members.forEach(member => {
      deletePromises.push(deleteDoc(doc(db, 'org_members', member.id)));
      deletePromises.push(updateDoc(doc(db, 'users', member.userId), {
        orgId: null
      }));
    });

    // 4. Delete Requests
    requests.forEach(req => {
      deletePromises.push(deleteDoc(doc(db, 'org_requests', req.id)));
    });

    await Promise.all(deletePromises);

    // 5. Delete Organization itself
    await deleteDoc(doc(db, 'organizations', organization.id));
  };

  const inviteMember = async (email) => {
    if (!auth.currentUser || !organization) throw new Error("Must be logged in and part of an org");
    
    // Check if they are already a member
    const existingQ = query(collection(db, 'org_members'), where('orgId', '==', organization.id), where('userEmail', '==', email));
    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) throw new Error("User is already a member");

    // We can directly create a pending request that can be approved, or we can just create an approved member if we want direct invite.
    // The plan says "directly create a pending invite or user record". Let's create an approved member record with no userId if they haven't signed up yet,
    // or if they have, link it. Since we can't easily query users by email securely client-side in most default setups, we'll just create a pending request
    // that the user can accept, or an auto-approved request.
    // Let's create an org_requests record that is pre-approved or just a direct invite record.
    // Given the prompt: "add inviteMember(email) to directly create a pending invite or user record".
    // I'll create a member record with just an email, and when they sign in, they get linked.
    // Actually, Firestore rules might require userId. Let's create a pending request instead.
    await addDoc(collection(db, 'org_requests'), {
      orgId: organization.id,
      orgName: organization.name,
      userEmail: email,
      status: 'pending_invite',
      createdAt: new Date().toISOString()
    });
  };

  const updateMemberRole = async (memberId, newRole) => {
    if (!auth.currentUser || !organization || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    await updateDoc(doc(db, 'org_members', memberId), {
      role: newRole
    });
  };

  const bulkImportMembers = async (csvData) => {
    if (!auth.currentUser || !organization || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    
    // csvData is expected to be an array of objects: { userEmail, role, teamName }
    const teamsQ = query(collection(db, 'teams'), where('orgId', '==', organization.id));
    const teamsSnap = await getDocs(teamsQ);
    const existingTeams = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const batchPromises = [];
    
    for (const row of csvData) {
      if (!row.userEmail) continue;
      
      let teamId = null;
      if (row.teamName) {
        let team = existingTeams.find(t => t.name.toLowerCase() === row.teamName.trim().toLowerCase());
        if (!team) {
          const teamRef = await addDoc(collection(db, 'teams'), {
            name: row.teamName.trim(),
            orgId: organization.id,
            createdBy: auth.currentUser.uid,
            createdAt: new Date().toISOString()
          });
          team = { id: teamRef.id, name: row.teamName.trim() };
          existingTeams.push(team);
        }
        teamId = team.id;
      }

      // Check if member already exists by email
      const memberQ = query(collection(db, 'org_members'), where('orgId', '==', organization.id), where('userEmail', '==', row.userEmail.trim()));
      const memberSnap = await getDocs(memberQ);
      
      if (memberSnap.empty) {
        // Invite them
        batchPromises.push(addDoc(collection(db, 'org_requests'), {
          orgId: organization.id,
          orgName: organization.name,
          userEmail: row.userEmail.trim(),
          userName: row.userName || '',
          status: 'pending_invite',
          intendedRole: row.role || 'Member',
          intendedTeamId: teamId,
          createdAt: new Date().toISOString()
        }));
      } else {
        // Update them
        const docId = memberSnap.docs[0].id;
        const updates = {};
        if (row.role) updates.role = row.role;
        if (teamId) {
          const currentTeamIds = memberSnap.docs[0].data().teamIds || [];
          if (!currentTeamIds.includes(teamId)) {
            currentTeamIds.push(teamId);
            updates.teamIds = currentTeamIds;
          }
        }
        if (Object.keys(updates).length > 0) {
          batchPromises.push(updateDoc(doc(db, 'org_members', docId), updates));
        }
      }
    }
    
    await Promise.all(batchPromises);
  };


  return { 
    organization, 
    orgMember, 
    members, 
    requests, 
    loading, 
    createOrganization,
    checkDomainAndRequestAccess,
    approveRequest,
    rejectRequest,
    transferOwnership,
    deleteOrganization,
    inviteMember,
    updateMemberRole,
    bulkImportMembers
  };
}
